import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../features/auth/authSlice";
import { toast } from "react-toastify";
import AuthSplashView from "../views/AuthSplashView";
import ApiCall from "../common/ApiCall";
import ScrollToTop from "../components/ScrollToTop";
import AvatarModal from "../components/AvatarModal";

const LoginController = lazy(() => import("../controllers/LoginController"));
const AdminLoginController = lazy(
  () => import("../controllers/AdminLoginController"),
);
const DashboardController = lazy(
  () => import("../controllers/DashboardController"),
);
const ForgotController = lazy(() => import("../controllers/ForgotController"));
const EmailVerification = lazy(() => import("../components/EmailVerification"));
const ResetController = lazy(() => import("../controllers/ResetController"));
const ProfileController = lazy(
  () => import("../controllers/ProfileController"),
);
const CompeletProfileController = lazy(
  () => import("../controllers/CompeletProfileController"),
);
const AdminController = lazy(() => import("../controllers/AdminController"));
const ApiController = lazy(() => import("../controllers/ApiController"));
const ResearchController = lazy(
  () => import("../controllers/ResearchController"),
);
const CareerController = lazy(() => import("../controllers/CareerController"));
const SocialShareController = lazy(
  () => import("../controllers/SocialShareController"),
);
const WebsiteConnectController = lazy(
  () => import("../controllers/WebsiteConnectController"),
);
const DreamTypesController = lazy(
  () => import("../controllers/DreamTypesController"),
);
const DreamDictionaryController = lazy(
  () => import("../controllers/DreamDictionaryController"),
);
const DreamUnderstandController = lazy(
  () => import("../controllers/DreamUnderstandController"),
);
const TransferAccountController = lazy(
  () => import("../controllers/TransferAccountController"),
);
const ProfileDetailController = lazy(
  () => import("../controllers/ProfileDetailController"),
);
const ChatsController = lazy(() => import("../controllers/ChatsController"));
const ActivityController = lazy(
  () => import("../controllers/ActivityController"),
);
const DreamLogController = lazy(
  () => import("../controllers/DreamLogController"),
);
const UserController = lazy(() => import("../controllers/UserController"));
const AccessLevelController = lazy(
  () => import("../controllers/AccessLevelController"),
);
const CommercePanelController = lazy(
  () => import("../controllers/CommercePanelController"),
);
const UserPaymentsPanelController = lazy(
  () => import("../controllers/UserPaymentsPanelController"),
);
const BugController = lazy(() => import("../controllers/BugController"));
const DreamResearhController = lazy(
  () => import("../controllers/DreamResearchController"),
);
const ProductController = lazy(
  () => import("../controllers/ProductController"),
);
const MembershipController = lazy(
  () => import("../controllers/MembershipController"),
);
const PurchaseController = lazy(
  () => import("../controllers/PurchaseController"),
);
const FrontSliderController = lazy(
  () => import("../controllers/FrontSliderController"),
);
const AvatarsController = lazy(
  () => import("../controllers/AvatarsController"),
);
const BusinessPaymentSetupController = lazy(
  () => import("../controllers/BusinessPaymentSetupController"),
);
const BusinessDashboardController = lazy(
  () => import("../controllers/BusinessDashboardController"),
);
const WalletController = lazy(() => import("../controllers/WalletController"));
const RegistrationSuccessController = lazy(
  () => import("../controllers/RegistrationSuccessController"),
);
const AdminSettingController = lazy(
  () => import("../controllers/AdminSettingController"),
);
const AdvertisingPanelController = lazy(
  () => import("../controllers/AdvertisingPanelController"),
);
const FrontpageSettingsController = lazy(
  () => import("../controllers/FrontpageSettingsController"),
);
const LegalSettingsController = lazy(
  () => import("../controllers/LegalSettingsController"),
);
const GeneralSettingsController = lazy(
  () => import("../controllers/GeneralSettingsController"),
);
const FooterSettingsController = lazy(
  () => import("../controllers/FooterSettingsController"),
);
const RevenueStreamsController = lazy(
  () => import("../controllers/RevenueStreamsController"),
);
const SubmitFeedbackController = lazy(
  () => import("../controllers/SubmitFeedbackController"),
);
const SetPasswordController = lazy(
  () => import("../controllers/SetPasswordController"),
);
const SocialMediaView = lazy(() => import("../views/Admin/SocialMediaView"));
const QAView = lazy(() => import("../views/QAView"));
const QAContentView = lazy(() => import("../views/Admin/QAContentView"));
const CoppaPostRegistrationController = lazy(
  () => import("../controllers/CoppaPostRegistrationController"),
);
const AdminApiController = lazy(
  () => import("../controllers/AdminApiController"),
);
const TermsOfServiceView = lazy(() => import("../views/TermsOfServiceView"));
const PrivacyPolicyView = lazy(() => import("../views/PrivacyPolicyView"));

const isProfileComplete = (user) => {
  // If impersonating, always bypass profile completion check
  if (localStorage.getItem("is_impersonating") === "1") return true;

  if (!user) return false;

  const requiredFields = [
    "dob",
    "location",
    "user_coordinates",
    "gender",
    "display_name",
  ];
  return !requiredFields.some(
    (field) =>
      user[field] === null || user[field] === undefined || user[field] === "",
  );
};

const AuthenticatedRoute = ({
  children,
  requireCompleteProfile = true,
  requiredPermissions = [],
}) => {
  // We include isImpersonating in selector to ensure re-render when it changes
  const { isAuthenticated, user, isImpersonating } = useSelector(
    (state) => state.auth,
  );

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (requireCompleteProfile && !isProfileComplete(user)) {
    return <Navigate to="/complete-profile" replace />;
  }

  if (requiredPermissions.length > 0) {
    const userPermissions = user?.permissions || [];

    const hasAnyPermission = requiredPermissions.some((perm) =>
      userPermissions.includes(perm),
    );

    if (!hasAnyPermission) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

// Special route for complete-profile that redirects if profile is already complete
const CompleteProfileRoute = ({ children }) => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (isProfileComplete(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
};

const AdminRoute = ({ children, requiredPermissions = [] }) => {
  // Include isImpersonating for re-renders
  const { isAuthenticated, user, isImpersonating } = useSelector(
    (state) => state.auth,
  );

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (!isProfileComplete(user)) {
    return <Navigate to="/complete-profile" replace />;
  }

  const userRoles = user?.roles?.map((r) => r.name) || [];

  if (userRoles.includes("User") || userRoles.length === 0) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredPermissions.length > 0) {
    const userPermissions = user?.permissions || [];

    const hasAnyPermission = requiredPermissions.some((perm) =>
      userPermissions.includes(perm),
    );

    if (!hasAnyPermission) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

const hasAnyPermission = (user, requiredPermissions = []) => {
  if (!user?.permissions || !Array.isArray(user.permissions)) return false;
  return requiredPermissions.some((permission) =>
    user.permissions.includes(permission),
  );
};

const DashboardRoute = ({ children }) => {
  const { isAuthenticated, user, isImpersonating } = useSelector(
    (state) => state.auth,
  );
  const dispatch = useDispatch();

  const toastShownRef = useRef(false);

  const userPermissions = user?.permissions || [];
  const hasFrontpagePermission = userPermissions.includes("frontend.frontpage");
  const hasProfilePermission = userPermissions.includes("frontend.profile");
  const hasLoggedOutRef = useRef(false);

  //side effects only inside useEffect (toast + logout)
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (!isProfileComplete(user)) return;

    if (!hasFrontpagePermission) {
      if (!toastShownRef.current) {
        toastShownRef.current = true;
        toast.error(
          "Access Denied: You do not have permission to access dashboard",
          {
            position: "top-right",
            autoClose: 3000,
            theme: "light",
          },
        );
      }

      if (!hasProfilePermission && !hasLoggedOutRef.current) {
        hasLoggedOutRef.current = true;
        dispatch(logout());
      }
    }
  }, [
    isAuthenticated,
    user,
    hasFrontpagePermission,
    hasProfilePermission,
    dispatch,
    isImpersonating,
  ]);

  //redirects (no side effects here)
  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (!isProfileComplete(user))
    return <Navigate to="/complete-profile" replace />;

  // if no dashboard permission:
  if (!hasFrontpagePermission) {
    // has profile permission => go profile
    if (hasProfilePermission) return <Navigate to="/profile" replace />;

    // no both permissions => logout already triggered in useEffect
    return <Navigate to="/" replace />;
  }

  return children;
};

const LoadingFallback = () => {
  return (
    <div id="initial-loader">
      <div className="loader-content">
        <img
          src={`${process.env.PUBLIC_URL}/assets/images/loader-logo.png`}
          alt="DreamClouds Logo"
          className="loading-logo"
        />
      </div>
    </div>
  );
};

const AppRoutes = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [isSplashActive, setIsSplashActive] = useState(false);
  const [isSplashLoading, setIsSplashLoading] = useState(true);
  const [splashContent, setSplashContent] = useState("");
  const [splashLogo, setSplashLogo] = useState(null);
  const [isIpAllowed, setIsIpAllowed] = useState(false);

  useEffect(() => {
    const fetchSplashContent = async () => {
      try {
        const res = await ApiCall(
          "GET",
          "frontpage-content?type=splash",
          null,
          null,
          true,
        );

        if (res?.data?.success && res.data.data.contents?.length > 0) {
          const splashData = res.data.data.contents[0];

          setIsSplashActive(splashData.status === 1);
          setSplashContent(splashData.text || "");
          setIsIpAllowed(splashData.is_ip_allowed || false);
        }

        const logoRes = await ApiCall("GET", "get-logo", null, null, true);
        if (logoRes?.data?.success && logoRes.data.data?.length > 0) {
          const mainLogo =
            logoRes.data.data.find(
              (logo) => logo.type === "main" || logo.type === "auth",
            ) || logoRes.data.data[0];
          if (mainLogo?.logo_url) {
            setSplashLogo(mainLogo.logo_url);
          }
        }
      } catch (err) {
        console.error("Error fetching splash content:", err);
      } finally {
        setIsSplashLoading(false);
      }
    };

    fetchSplashContent();
  }, []);

  // Determine if splash should be bypassed (Strictly IP-based or Admin role)
  const userRoles =
    user?.roles?.map((r) => (typeof r === "string" ? r : r.name)) || [];
  const isAdmin = userRoles.some(
    (role) => role === "Admin" || role === "SuperAdmin" || role === "admin",
  );
  const isBypass = isIpAllowed || isAdmin;

  // Automatically logout if splash is active and user is not bypassed
  useEffect(() => {
    if (isSplashActive && user && !isBypass) {
      dispatch(logout());
    }
  }, [isSplashActive, user, isBypass, dispatch]);

  if (isSplashLoading) {
    return <LoadingFallback />;
  }

  const isAdminPath = window.location.pathname === "/dc-admin";

  if (isSplashActive && !isBypass && !isAdminPath) {
    return (
      <AuthSplashView
        splashContent={splashContent}
        setIsSplashActive={setIsSplashActive}
        logo={splashLogo}
      />
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/"
            element={
              <PublicRoute>
                <LoginController />
              </PublicRoute>
            }
          />

          <Route
            path="/dc-admin"
            element={
              <PublicRoute>
                <AdminLoginController />
              </PublicRoute>
            }
          />

          <Route
            path="/complete-profile"
            element={
              <CompleteProfileRoute>
                <CompeletProfileController />
              </CompleteProfileRoute>
            }
          />

          <Route
            path="/email-verification/:status"
            element={<EmailVerification />}
          />

          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotController />
              </PublicRoute>
            }
          />

          <Route
            path="/set-password"
            element={
              <PublicRoute>
                <SetPasswordController />
              </PublicRoute>
            }
          />

          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetController />
              </PublicRoute>
            }
          />

          <Route
            path="/transfer-account"
            element={
              <PublicRoute>
                <TransferAccountController />
              </PublicRoute>
            }
          />

          <Route
            path="/registration-success"
            element={
              <PublicRoute>
                <RegistrationSuccessController />
              </PublicRoute>
            }
          />

          <Route
            path="/coppa-registration"
            element={<CoppaPostRegistrationController />}
          />

          {/* Authenticated Routes */}
          <Route
            path="/dashboard"
            element={
              <DashboardRoute>
                <DashboardController />
              </DashboardRoute>
            }
          />

          <Route
            path="/profile-setting"
            element={
              <AuthenticatedRoute requiredPermissions={["frontend.setting"]}>
                <ProfileController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <AuthenticatedRoute requiredPermissions={["frontend.dashboard"]}>
                <ProfileController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/memberships"
            element={
              <AuthenticatedRoute>
                <MembershipController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/my-subscriptions"
            element={
              <AuthenticatedRoute>
                <MembershipController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/my-purchases"
            element={
              <AuthenticatedRoute>
                <PurchaseController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/wallet"
            element={
              <AuthenticatedRoute>
                <WalletController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/api"
            element={
              <AuthenticatedRoute
                requiredPermissions={["frontend.frontend_api_access"]}
              >
                <ApiController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/research"
            element={
              <AuthenticatedRoute>
                <ResearchController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/careers"
            element={
              <AuthenticatedRoute>
                <CareerController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/social-share/:id1"
            element={<SocialShareController />}
          />
          <Route
            path="/social-share/:id1/:id2"
            element={<SocialShareController />}
          />

          <Route
            path="/website-connect"
            element={
              <AuthenticatedRoute>
                <WebsiteConnectController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/dream-understand"
            element={
              <AuthenticatedRoute>
                <DreamUnderstandController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/dream-dictionary"
            element={
              <AuthenticatedRoute>
                <DreamDictionaryController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/dream-types"
            element={
              <AuthenticatedRoute>
                <DreamTypesController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/admin-panel"
            element={
              <AdminRoute requiredPermissions={[]}>
                <AdminController />
              </AdminRoute>
            }
          />

          <Route
            path="/activity-log"
            element={
              <AdminRoute requiredPermissions={["user_activity.activity_log"]}>
                <ActivityController />
              </AdminRoute>
            }
          />

          <Route
            path="/dream-log"
            element={
              <AdminRoute requiredPermissions={["user_activity.dream_log"]}>
                <DreamLogController />
              </AdminRoute>
            }
          />

          <Route
            path="/bugs"
            element={
              <AdminRoute requiredPermissions={["user_activity.review_bugs"]}>
                <ActivityController />
              </AdminRoute>
            }
          />

          <Route
            path="/users-list"
            element={
              <AdminRoute requiredPermissions={["user_access.users"]}>
                <UserController />
              </AdminRoute>
            }
          />

          <Route
            path="/add-user"
            element={
              <AdminRoute requiredPermissions={["user_access.users"]}>
                <UserController />
              </AdminRoute>
            }
          />

          <Route
            path="/access-levels"
            element={
              <AdminRoute requiredPermissions={["user_access.access_levels"]}>
                <AccessLevelController />
              </AdminRoute>
            }
          />

          <Route
            path="/dream-research"
            element={
              <AdminRoute>
                <DreamResearhController />
              </AdminRoute>
            }
          />

          <Route
            path="/commerce-panel"
            element={
              <AdminRoute requiredPermissions={["commerce_panel"]}>
                <CommercePanelController />
              </AdminRoute>
            }
          />

          <Route
            path="/user-payments-panel"
            element={
              <AdminRoute
                requiredPermissions={[
                  "commerce_panel",
                  "user_payments",
                  "user_payments.ability_to_refund",
                  "user_payments.ability_to_comp_products",
                  "user_payments.ability_to_comp_subscriptions",
                ]}
              >
                <UserPaymentsPanelController />
              </AdminRoute>
            }
          />

          <Route
            path="/advertising-panel"
            element={
              <AdminRoute requiredPermissions={["commerce_panel"]}>
                <AdvertisingPanelController />
              </AdminRoute>
            }
          />

          <Route
            path="/revenue-streams"
            element={
              <AdminRoute
                requiredPermissions={[
                  "revenue_streams.revenue",
                  "revenue_streams.banking",
                ]}
              >
                <RevenueStreamsController />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/api-management"
            element={
              <AdminRoute
                requiredPermissions={[
                  "api_management",
                  "api_management.api_keys",
                  "api_management.usage",
                  "api_management.account_info",
                  "api_management.global_calls",
                ]}
              >
                <AdminApiController />
              </AdminRoute>
            }
          />

          <Route
            path="/frontpage-slider"
            element={
              <AdminRoute requiredPermissions={["frontpage_sliders"]}>
                <FrontSliderController />
              </AdminRoute>
            }
          />

          <Route
            path="/general-settings"
            element={
              <AdminRoute requiredPermissions={["frontpage_sliders"]}>
                <GeneralSettingsController />
              </AdminRoute>
            }
          />

          <Route
            path="/frontpage-settings"
            element={
              <AdminRoute requiredPermissions={["frontpage_sliders"]}>
                <FrontpageSettingsController />
              </AdminRoute>
            }
          />

          <Route
            path="/legal-settings"
            element={
              <AdminRoute>
                <LegalSettingsController />
              </AdminRoute>
            }
          />

          <Route
            path="/footer-settings"
            element={
              <AdminRoute requiredPermissions={["frontpage_sliders"]}>
                <FooterSettingsController />
              </AdminRoute>
            }
          />

          <Route
            path="/avatars-panel"
            element={
              <AdminRoute requiredPermissions={["manage_avatars"]}>
                <AvatarsController />
              </AdminRoute>
            }
          />

          <Route
            path="/admin-setting"
            element={
              <AdminRoute>
                <AdminSettingController />
              </AdminRoute>
            }
          />

          <Route
            path="/social-media-links"
            element={
              <AdminRoute requiredPermissions={["frontpage_sliders"]}>
                <SocialMediaView />
              </AdminRoute>
            }
          />

          <Route
            path="/qa-content"
            element={
              <AdminRoute requiredPermissions={["frontpage_sliders"]}>
                <QAContentView />
              </AdminRoute>
            }
          />

          <Route
            path="/qa"
            element={
              <AuthenticatedRoute>
                <QAView />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/terms-of-service"
            element={
              <AuthenticatedRoute>
                <TermsOfServiceView />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/privacy-policy"
            element={
              <AuthenticatedRoute>
                <PrivacyPolicyView />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/profile/:id"
            element={
              <AuthenticatedRoute>
                <ProfileDetailController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/chats"
            element={
              <AuthenticatedRoute>
                <ChatsController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/add-bug"
            element={
              <AuthenticatedRoute>
                <BugController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/submit-feedback"
            element={
              <AuthenticatedRoute>
                <SubmitFeedbackController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/shop"
            element={
              <AuthenticatedRoute>
                <ProductController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/business/payment-setup"
            element={
              <AuthenticatedRoute>
                <BusinessPaymentSetupController />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/business/dashboard"
            element={
              <AuthenticatedRoute>
                <BusinessDashboardController />
              </AuthenticatedRoute>
            }
          />

          {/* Catch-all Route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRoutes;
